<template>
    <common-content-spacer height="medium" class="multiple-image-upload__wrapper">
        <common-content-wrapper width="extra-huge" class="multiple-image-upload__drag-and-drop" @drop.prevent="onDrop">
            <input
                ref="fileInput"
                type="file"
                multiple="multiple"
                style="display: none"
                accept="image/png,image/jpg,image/jpeg"
                @change="selectImages($event)"
            />
            <common-content-spacer height="small" :isCenter="true">
                <common-icon name="photo-upload" size="extra-larger"></common-icon>
                <div class="multiple-image-upload__text">{{ $t('components.images.multiple_image_upload.drag_and_drop') }}</div>
                <common-content-spacer height="tiny">
                    <common-buttons>
                        <common-button
                            :isSubmittable="false"
                            type="primary"
                            :label="$t('components.images.multiple_image_upload.browse_files')"
                            @click="$refs.fileInput.click()"
                        ></common-button>
                    </common-buttons>
                    <div class="multiple-image-upload__allowed-types">
                        {{ $t('components.images.multiple_image_upload.allowed_types') }}
                    </div>
                </common-content-spacer>
            </common-content-spacer>
        </common-content-wrapper>

        <div class="multiple-image-upload__images">
            <div v-for="(file, index) in allPhotos" :key="index">
                <div :class="photoClass(file)" @click="selectImage(file)">
                    <div class="multiple-image-upload__image">
                        <img :id="`photo_${index}`" :src="file.url" :alt="$t('alt.offer_details')" />
                        <div v-if="file.isUploading" class="multiple-image-upload__image--uploading"></div>
                        <div
                            v-if="file.isUploading"
                            :style="{ height: file.progress + '%' }"
                            class="multiple-image-upload__image--uploading-percentage"
                        ></div>
                        <div v-if="file.isUploading" class="multiple-image-upload__image--uploading-text">
                            {{ file.progress }}%
                        </div>
                        <div
                            v-if="file.isUploaded || file.error"
                            class="multiple-image-upload__image-remove"
                            @click="removeFile(file)"
                        >
                            <common-icon name="trash" size="large"></common-icon>
                        </div>
                    </div>

                    <div v-if="file.type === mainImageType" class="multiple-image-upload__main-icon">
                        <common-icon name="verified" size="small"></common-icon>
                    </div>
                </div>
                <div class="multiple-image-upload__main-input-checkbox">
                    <checkbox-input
                        v-if="shouldShowCheckbox(file)"
                        :label="$t('components.images.multiple_image_upload.set_main')"
                        :hasBigTapArea="true"
                        :isDisabled="file.type === mainImageType"
                        :value="isCheckboxChecked(file)"
                        @change="setAsMainImage(file)"
                    ></checkbox-input>

                    <span v-if="file.error" class="multiple-image-upload__invalid-file">
                        {{ $t('components.images.multiple_image_upload.invalid_file') }}
                    </span>
                </div>
            </div>

            <div v-for="index in FILES_MAX_NUMBER - allPhotos.length" :key="index">
                <div
                    class="multiple-image-upload__image multiple-image-upload__image--1placeholder"
                    @click="$refs.fileInput.click()"
                >
                    <common-icon name="photo" size="ultra-large"></common-icon>
                    <div class="multiple-image-upload__image--text">
                        {{ $t('components.images.multiple_image_upload.add_image_here') }}
                    </div>
                </div>
                <div class="multiple-image-upload__others-spacing"></div>
            </div>
        </div>
    </common-content-spacer>
</template>

<script>
    import UploadMixin from 'mixins/UploadMixin'
    import CheckboxInput from 'components/form-inputs/CheckboxInput'

    const EVENTS = ['dragenter', 'dragover', 'dragleave', 'drop']

    export default {
        mixins: [UploadMixin],

        components: { CheckboxInput },

        props: {
            photos: { type: Array, default: new Array() },
            uploadAPI: { type: Function, required: true },
            mainImageType: { type: String, required: true },
        },

        data: () => ({
            selectedImage: null,
        }),

        computed: {
            allPhotos() {
                return [
                    ...this.photos.map((photo) => ({
                        ...photo,
                        isUploaded: true,
                    })),
                    ...this.selectedFiles,
                ]
            },
        },

        methods: {
            selectImages(e) {
                if (this.isUploading) {
                    return this.$toast.warning(this.$t('common.errors.wait_for_upload_complete'))
                }
                const files = e.target.files || e.dataTransfer.files
                if (!files.length) return
                if (this.validateFiles(files, this.photos)) return
                this.convertAndUploadFiles()
                this.resetFileInputValue()
            },

            onDrop(e) {
                if (this.isUploading) {
                    return this.$toast.warning(this.$t('common.errors.wait_for_upload_complete'))
                }
                const files = e.dataTransfer.files
                if (this.validateFiles(files, this.photos)) return
                this.convertAndUploadFiles()
            },

            resetFileInputValue() {
                this.$refs.fileInput.value = ''
            },

            convertAndUploadFiles() {
                this.isUploading = true
                Promise.all(
                    this.selectedFiles.map((file) => {
                        if (!file.isUploaded) return this.convertFileToBase64(file)
                        return Promise.resolve(file)
                    }),
                ).then((files) => {
                    this.selectedFiles = files
                    const filesForUpload = this.selectedFiles.filter((file) => !file.isUploaded)
                    this.uploadAPI(filesForUpload)
                        .then(() => {
                            this.selectedFiles = []
                            this.emitEvent({ name: 'load-photos', payload: true })
                        })
                        .catch(() => {
                            this.$toast.error(this.$t('components.images.multiple_image_upload.invalid_image'))
                        })
                        .finally(() => {
                            this.isUploading = false
                        })
                })
            },

            removeFile(file) {
                if (file.error) {
                    return (this.selectedFiles = this.selectedFiles.filter((f) => file.base64File !== f.base64File))
                }
                if (file.type === this.mainImageType) {
                    return this.$toast.error(this.$t('components.images.multiple_image_upload.cant_delete_main_image'))
                }
                this.emitEvent({ name: 'remove-photo', payload: { id: file?.id || file?.file?.id } })
                this.selectedFiles = []
                this.resetFileInputValue()
            },

            photoClass(file) {
                return {
                    'multiple-image-upload__image-wrapper': true,
                    'multiple-image-upload__image-wrapper--selected':
                        file.type !== this.mainImageType && file.url === this.selectedImage?.url,
                    'multiple-image-upload__image-wrapper--main': file.type === this.mainImageType,
                    'multiple-image-upload__image-wrapper--error': file.error,
                }
            },

            selectImage(file) {
                this.selectedImage = file
            },

            shouldShowCheckbox(file) {
                return (file.url === this.selectedImage?.url || file.type === this.mainImageType) && !file.error
            },

            isCheckboxChecked(file) {
                return file.type === this.mainImageType
            },

            setAsMainImage(file) {
                this.emitEvent({ name: 'set-main-photo', payload: { id: file?.id || file?.file?.id } })
            },

            preventDefaults(e) {
                e.preventDefault()
            },
        },

        mounted() {
            EVENTS.forEach((eventName) => {
                document.body.addEventListener(eventName, this.preventDefaults)
            })
        },

        unmounted() {
            EVENTS.forEach((eventName) => {
                document.body.removeEventListener(eventName, this.preventDefaults)
            })
        },
    }
</script>

<style lang="scss" scoped>
    @import 'styles/components/images/_multiple-image-upload.scss';
</style>
